/**
 * WMS Dashboard Main Component
 *
 * Configurable warehouse dashboard with drag-and-drop widgets.
 * Designed for large screen displays in warehouse environments.
 *
 * Features:
 * - Customizable widget layout
 * - Drag-and-drop editing
 * - Multiple layout presets
 * - Fullscreen mode for TV/monitor displays
 * - TV/Kiosk mode with auto-rotation
 * - Auto-refreshing data via TanStack Query
 * - Dark theme optimized for visibility
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DashboardProvider, useDashboard } from './DashboardContext';
import { TVModeProvider, useTVMode } from './contexts/TVModeContext';
import { useDashboardScale } from './hooks/useDashboardScale';
import DashboardToolbar from './components/DashboardToolbar';
import DashboardGrid from './components/DashboardGrid';
import WidgetSelector from './components/WidgetSelector';

/**
 * Create a QueryClient instance for the dashboard.
 * Configured with defaults suitable for real-time dashboard data.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,       // 10 seconds before data is considered stale
      retry: 3,                // Retry failed requests 3 times
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      refetchOnWindowFocus: false, // Dashboard is always-on, don't refetch on focus
    },
  },
});

/**
 * TV Mode Progress Bar - shows time until next rotation
 */
const TVModeProgressBar: React.FC = () => {
  const { state } = useTVMode();
  const { config, rotationProgress } = state;

  if (!config.enabled || !config.showProgressBar || !config.autoRotate) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 h-1 bg-gray-800/50">
      <div
        className="h-full bg-blue-500 transition-all duration-100 ease-linear"
        style={{ width: `${rotationProgress}%` }}
      />
    </div>
  );
};

/**
 * TV Mode Page Dots - shows which layout is active
 */
const TVModePageDots: React.FC<{ total: number; current: number }> = ({ total, current }) => {
  const { state } = useTVMode();
  const { config } = state;

  if (!config.enabled || !config.showPageDots || total <= 1) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
      {Array.from({ length: total }).map((_, index) => (
        <div
          key={index}
          className={`
            w-2 h-2 rounded-full transition-all duration-300
            ${index === current
              ? 'bg-blue-400 scale-125'
              : 'bg-gray-500/50'
            }
          `}
        />
      ))}
    </div>
  );
};

/**
 * Dashboard Content Component (uses context)
 */
const DashboardContent: React.FC = () => {
  const { state } = useDashboard();
  const { isFullscreen, isEditMode, availableLayouts } = state;
  const [showWidgetSelector, setShowWidgetSelector] = useState(false);

  const tvMode = useTVMode();
  const isTVActive = tvMode.state.config.enabled;
  const hideHeader = isTVActive && tvMode.state.config.hideHeader;

  // Auto-scale for large displays (warehouse TVs, etc.)
  const dashboardScale = useDashboardScale();

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to close widget selector
      if (e.key === 'Escape') {
        if (showWidgetSelector) {
          setShowWidgetSelector(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showWidgetSelector]);

  return (
    <div
      className={`
        h-full w-full flex flex-col
        bg-gray-900 text-white
        ${isFullscreen || isTVActive ? 'fixed inset-0 z-50' : ''}
      `}
    >
      {/* Toolbar - hidden in TV mode (fades in on hover via CSS) */}
      <div
        className={`
          transition-opacity duration-300
          ${hideHeader ? 'opacity-0 hover:opacity-100 absolute top-0 left-0 right-0 z-50' : ''}
        `}
      >
        <DashboardToolbar onAddWidget={() => setShowWidgetSelector(true)} />
      </div>

      {/* Main grid area - scaled for large displays */}
      <div
        className={`flex-1 min-h-0 relative ${hideHeader ? 'pt-0' : ''}`}
        style={dashboardScale > 1 ? { zoom: dashboardScale } : undefined}
      >
        <DashboardGrid />

        {/* Edit mode indicator */}
        {isEditMode && !isTVActive && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-blue-600 rounded-full shadow-lg">
            <p className="text-sm text-white font-medium">
              Redigeringsmodus - Dra widgets for å flytte
            </p>
          </div>
        )}
      </div>

      {/* Widget selector panel */}
      <WidgetSelector
        isOpen={showWidgetSelector}
        onClose={() => setShowWidgetSelector(false)}
      />

      {/* TV Mode overlays */}
      {isTVActive && (
        <>
          <TVModeProgressBar />
          <TVModePageDots
            total={availableLayouts.length}
            current={tvMode?.state.currentRotationIndex ?? 0}
          />
        </>
      )}
    </div>
  );
};

/**
 * WMS Dashboard Component
 *
 * Main export - wraps content with all required providers:
 * 1. QueryClientProvider (TanStack Query for data fetching)
 * 2. DashboardProvider (layout state, widget management)
 * 3. TVModeProvider (TV/kiosk mode features)
 */
const WMSDashboard: React.FC = () => {
  const [layoutCount, setLayoutCount] = useState(1);

  const handleRotate = useCallback((nextIndex: number) => {
    // TV mode rotation - will be connected to DashboardContext.loadLayout
    // This is handled inside TVModeProvider/DashboardContent interaction
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <DashboardProvider>
        <TVModeProvider onRotate={handleRotate} layoutCount={layoutCount}>
          <DashboardContent />
        </TVModeProvider>
      </DashboardProvider>
    </QueryClientProvider>
  );
};

export default WMSDashboard;
